import { PrimVoicesProvider, usePrimVoices, WebSocketClientConfig } from "primvoices-react";
import { useEffect, useState } from "react";
import clsx from "clsx";
import { useNavigate, useParams } from "react-router-dom";

import { AgentMonitoring } from "./AgentMonitoring";
import { Text, View } from "../base";
import { Icon } from "../base/Icon";
import { InputField } from "../base/InputField";
import { useAppSelector } from "../../redux";
import { slugify } from "../../utils/strings";
import { API_URL } from "../../config";
import { useApiRequest } from "../../hooks/useApiRequest";
import { PhoneNumber } from "../../redux/reducers/userSlice";
import { AgentEnvironment } from "../../redux/reducers/agentEnvironmentSlice";
import { getGradientForName } from "../../utils/gradients";

type TestMode = "web" | "inbound" | "outbound";

type ModeToggleProps = {
  value: TestMode;
  onChange: (mode: TestMode) => void;
  isDisabled?: boolean;
};

const ModeToggle = ({ value, onChange, isDisabled }: ModeToggleProps) => {
  const modes: { id: TestMode; label: string; icon: string }[] = [
    { id: "web", label: "Web", icon: "language" },
    { id: "inbound", label: "Inbound", icon: "phone-inbound" },
    { id: "outbound", label: "Outbound", icon: "phone-outbound" },
  ];

  return (
    <View className="flex items-center w-full bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
      {modes.map((mode) => (
        <button
          key={mode.id}
          onClick={() => !isDisabled && onChange(mode.id)}
          className={clsx(
            "flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200",
            value === mode.id
              ? "bg-white dark:bg-gray-600 text-brand dark:text-brand-light shadow-sm"
              : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200",
            isDisabled && "cursor-not-allowed opacity-50",
          )}
          disabled={isDisabled}
        >
          <Icon
            name={mode.icon}
            size={16}
          />
          {mode.label}
        </button>
      ))}
    </View>
  );
};

type CompactEnvironmentDropdownProps = {
  environments: AgentEnvironment[];
  value: string;
  onChange: (id: string) => void;
};

const CompactEnvironmentDropdown = ({ environments, value, onChange }: CompactEnvironmentDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedEnvironment = environments.find((env) => env.id === value);
  const selectedGradient = selectedEnvironment ? getGradientForName(selectedEnvironment.name) : null;

  return (
    <View className="relative flex-1">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between gap-2 w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors"
      >
        <View className="flex items-center gap-2 min-w-0">
          {selectedEnvironment && selectedGradient ? (
            <View
              className={clsx(
                "w-5 h-5 rounded-full bg-gradient-to-br flex items-center justify-center flex-shrink-0",
                selectedGradient.from,
                selectedGradient.to,
                selectedGradient.darkFrom,
                selectedGradient.darkTo,
              )}
            >
              <span className="text-xs font-bold text-white">{selectedEnvironment.name.substring(0, 1).toUpperCase()}</span>
            </View>
          ) : (
            <Icon
              name="layers"
              size={14}
              className="text-gray-500 dark:text-gray-400 flex-shrink-0"
            />
          )}
          <span className="truncate">{selectedEnvironment?.name || "Select"}</span>
        </View>
        <Icon
          name={isOpen ? "chevron-up" : "chevron-down"}
          size={14}
          className="text-gray-400 flex-shrink-0"
        />
      </button>
      {isOpen && (
        <>
          <View
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <View className="absolute z-50 top-full left-0 mt-1 min-w-[180px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden">
            {environments.map((env) => (
              <button
                key={env.id}
                onClick={() => {
                  onChange(env.id);
                  setIsOpen(false);
                }}
                className={clsx(
                  "w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors",
                  value === env.id
                    ? "bg-brand/10 text-brand dark:text-brand-light"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700",
                )}
              >
                {(() => {
                  const gradient = getGradientForName(env.name);
                  return (
                    <View
                      className={clsx(
                        "w-4 h-4 rounded-full bg-gradient-to-br flex items-center justify-center flex-shrink-0",
                        gradient.from,
                        gradient.to,
                        gradient.darkFrom,
                        gradient.darkTo,
                      )}
                    >
                      <span className="text-[0.5rem] font-bold text-white">{env.name.substring(0, 1).toUpperCase()}</span>
                    </View>
                  );
                })()}
                <span className="capitalize">{env.name}</span>
                {env.phoneNumber && (
                  <Icon
                    name="phone"
                    size={12}
                    className="ml-auto text-gray-400"
                  />
                )}
              </button>
            ))}
          </View>
        </>
      )}
    </View>
  );
};

type AgentDebuggerInternalProps = {
  functionId?: string;
  environmentId?: string;
  isOpen: boolean;
  onClick: () => void;
  onChangeEnvironment: (environment: string) => void;
};

export const AgentDebuggerInternal = ({ functionId, environmentId, isOpen, onClick, onChangeEnvironment }: AgentDebuggerInternalProps) => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [testMode, setTestMode] = useState<TestMode>("web");
  const [inputMessage, setInputMessage] = useState("");
  const [outboundPhoneNumber, setOutboundPhoneNumber] = useState("");
  const agentEnvironments = useAppSelector((state) => Object.values(state.agentEnvironment.entities));
  const agentEnvironment = useAppSelector((state) => (environmentId ? state.agentEnvironment.entities[environmentId] : null));
  const { connect, disconnect, startListening, stopListening, sendTextEvent, isConnected, isListening } = usePrimVoices();
  const configureDebugPhoneNumber = useApiRequest<{ data: PhoneNumber }>();
  const startOutboundCall = useApiRequest<{ data: { sessionId: string } }>();

  const onClickStart = async () => {
    try {
      await disconnect();
      await connect();
    } catch (err) {
      console.error("Failed to start conversation:", err);
    }
  };

  const onClickStop = async () => {
    try {
      await stopListening();
      await disconnect();
      setInputMessage("");
    } catch (err) {
      console.error("Failed to stop conversation:", err);
    }
  };

  const onClickMic = async () => {
    try {
      if (isListening) {
        await stopListening();
      } else {
        await startListening();
      }
    } catch (err) {
      console.error("Failed to toggle microphone:", err);
    }
  };

  const onClickSendMessage = async () => {
    if (!inputMessage.trim()) return;

    try {
      const userMessage = inputMessage.trim();
      await sendTextEvent(userMessage);
      setInputMessage("");
    } catch (err) {
      console.error("Failed to send message:", err);
    }
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      onClickSendMessage();
    }
  };

  const onClickConfigureInbound = async () => {
    try {
      await configureDebugPhoneNumber.fetch(`/v1/agents/${agentId}/debugPhoneNumber`, {
        method: "POST",
        body: {
          environment: `${slugify(agentEnvironment?.name || "")}|${functionId}`,
        },
      });
    } catch (err) {
      console.error("Failed to fetch phone number:", err);
    }
  };

  const onClickDial = async () => {
    if (!outboundPhoneNumber.trim() || !agentEnvironment?.phoneNumber) return;

    try {
      await startOutboundCall.fetch(`/v1/agents/${agentId}/sessions/start`, {
        method: "POST",
        body: {
          inputType: "phone",
          inputParameters: {
            fromPhoneNumber: agentEnvironment.phoneNumber,
            phoneNumber: outboundPhoneNumber.trim(),
          },
          environment: `${slugify(agentEnvironment?.name || "")}|${functionId}`,
          parameters: {},
        },
      });
    } catch (err) {
      console.error("Failed to start outbound call:", err);
    }
  };

  useEffect(() => {
    if (!environmentId && agentEnvironments.length > 0) {
      onChangeEnvironment(agentEnvironments[0].id);
    }
  }, [environmentId, agentEnvironments]);

  // Auto-configure inbound phone number when switching to inbound mode
  useEffect(() => {
    if (testMode === "inbound" && agentEnvironment && !configureDebugPhoneNumber.data) {
      onClickConfigureInbound();
    }
  }, [testMode, agentEnvironment]);

  // Determine action button state based on mode
  const getActionButton = () => {
    switch (testMode) {
      case "web":
        return {
          label: isConnected ? "Stop" : "Start",
          icon: isConnected ? "stop" : "play",
          onClick: isConnected ? onClickStop : onClickStart,
          isDisabled: false,
          variant: isConnected ? "danger" : "primary",
        };
      case "inbound":
        return {
          label: "Waiting for Call",
          icon: "phone-inbound",
          onClick: () => {},
          isDisabled: true,
          variant: "secondary",
        };
      case "outbound":
        return {
          label: startOutboundCall.isLoading ? "Dialing..." : "Dial",
          icon: "phone-outbound",
          onClick: onClickDial,
          isDisabled: !agentEnvironment?.phoneNumber || !outboundPhoneNumber.trim() || startOutboundCall.isLoading,
          variant: "primary",
        };
    }
  };

  const actionButton = getActionButton();

  return (
    <View
      className={clsx(
        "relative h-full bg-white dark:bg-gray-800 shadow-lg border-l border-gray-200 dark:border-gray-700 transition-all duration-300 ease-in-out",
        isOpen ? "w-[500px]" : "w-[48px]",
        "hidden md:flex flex-col overflow-hidden",
      )}
    >
      {/* Collapsed State */}
      {!isOpen && (
        <View className="flex flex-col items-center h-full">
          <button
            type="button"
            onClick={onClick}
            aria-label="Expand debug sidebar"
            className="p-2 mt-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <Icon
              name="chevron-left"
              size={18}
              className="text-gray-500 dark:text-gray-400"
            />
          </button>
          <Text className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-sm font-medium text-gray-700 dark:text-gray-300 -rotate-90 whitespace-nowrap">
            Debugger
          </Text>
        </View>
      )}

      {/* Expanded State */}
      {isOpen && (
        <View className="flex flex-col h-full w-full overflow-hidden">
          {/* Header Controls */}
          <View className="flex flex-col gap-3 py-3 px-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            {/* Row 1: Collapse Button + Mode Toggle */}
            <View className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClick}
                aria-label="Collapse debug sidebar"
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0"
              >
                <Icon
                  name="chevron-right"
                  size={18}
                  className="text-gray-500 dark:text-gray-400"
                />
              </button>
              <ModeToggle
                value={testMode}
                onChange={setTestMode}
                isDisabled={isConnected}
              />
            </View>

            {/* Row 2: Environment + Action */}
            <View className="flex items-center gap-3">
              <CompactEnvironmentDropdown
                environments={agentEnvironments}
                value={environmentId || ""}
                onChange={onChangeEnvironment}
              />
              {/* Mute Button - show in web mode, inactive until session starts */}
              {testMode === "web" && (
                <button
                  onClick={isConnected ? onClickMic : undefined}
                  disabled={!isConnected}
                  title={!isConnected ? "Start session to enable microphone" : isListening ? "Mute microphone" : "Unmute microphone"}
                  className={clsx(
                    "flex items-center justify-center h-9 w-9 rounded-lg transition-colors flex-shrink-0 border",
                    !isConnected
                      ? "bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700 cursor-not-allowed opacity-40"
                      : isListening
                        ? "bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/50"
                        : "bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700",
                  )}
                >
                  <Icon
                    name={isListening ? "mic" : "mic-off"}
                    size={16}
                  />
                </button>
              )}

              {/* Action Button */}
              <button
                onClick={actionButton.onClick}
                disabled={actionButton.isDisabled}
                className={clsx(
                  "flex items-center gap-1.5 h-9 px-3.5 rounded-lg transition-colors text-sm font-medium flex-shrink-0 border",
                  actionButton.variant === "danger" &&
                    "bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/50",
                  actionButton.variant === "primary" &&
                    !actionButton.isDisabled &&
                    "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/50",
                  (actionButton.variant === "secondary" || (actionButton.variant === "primary" && actionButton.isDisabled)) &&
                    "bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700 cursor-not-allowed opacity-50",
                )}
              >
                <Icon
                  name={actionButton.icon}
                  size={14}
                />
                {actionButton.label}
              </button>
            </View>
          </View>

          {/* Inbound Mode Info Panel */}
          {testMode === "inbound" && (
            <View className="flex flex-col gap-3 p-4 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              {configureDebugPhoneNumber.isLoading ? (
                <View className="flex items-center gap-2">
                  <View className="w-4 h-4 border-2 border-brand border-t-transparent rounded-full animate-spin" />
                  <Text className="text-sm text-gray-600 dark:text-gray-400">Configuring debug phone number...</Text>
                </View>
              ) : configureDebugPhoneNumber.data?.data.phoneNumber ? (
                <View className="flex items-center justify-between">
                  <View className="flex flex-col gap-1">
                    <Text className="text-xs text-gray-500 dark:text-gray-400">Call this number to test your agent:</Text>
                    <Text className="text-lg font-semibold text-gray-900 dark:text-white">{configureDebugPhoneNumber.data.data.phoneNumber}</Text>
                  </View>
                  <button
                    onClick={() => navigator.clipboard.writeText(configureDebugPhoneNumber.data?.data.phoneNumber || "")}
                    title="Copy phone number"
                    className="flex items-center justify-center h-8 w-8 rounded-lg text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    <Icon
                      name="copy"
                      size={16}
                    />
                  </button>
                </View>
              ) : (
                <View className="flex items-center justify-between gap-2 flex-wrap">
                  <View className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                    <Icon
                      name="warning-amber"
                      size={16}
                      className="flex-shrink-0"
                    />
                    <Text className="text-sm">No phone number assigned.</Text>
                  </View>
                  <button
                    onClick={() => navigate("/profile")}
                    className="flex flex-row items-center gap-1 px-2.5 py-1 rounded-lg text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/50 transition-colors flex-shrink-0"
                  >
                    Configure in Profile
                    <Icon
                      name="arrow-right"
                      size={14}
                    />
                  </button>
                </View>
              )}
            </View>
          )}

          {/* Outbound Mode Panel */}
          {testMode === "outbound" && (
            <View className="flex flex-col gap-3 p-4 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              {!agentEnvironment?.phoneNumber ? (
                <View className="flex items-center justify-between gap-2 flex-wrap">
                  <View className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                    <Icon
                      name="warning-amber"
                      size={16}
                      className="flex-shrink-0"
                    />
                    <Text className="text-sm">This environment has no phone number configured.</Text>
                  </View>
                  <button
                    onClick={() => navigate(`/agents/${agentId}/environments`)}
                    className="flex flex-row items-center gap-1 px-2.5 py-1 rounded-lg text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/50 transition-colors flex-shrink-0"
                  >
                    Configure in Environments
                    <Icon
                      name="arrow-right"
                      size={14}
                    />
                  </button>
                </View>
              ) : (
                <>
                  <View className="flex flex-col gap-1">
                    <Text className="text-xs text-gray-500 dark:text-gray-400">Enter the phone number to call:</Text>
                    <View className="flex items-center gap-3">
                      <InputField
                        value={outboundPhoneNumber}
                        onChange={setOutboundPhoneNumber}
                        placeholder="+1 (555) 000-0000"
                        className="flex-1 h-10"
                      />
                      <button
                        onClick={onClickDial}
                        disabled={!outboundPhoneNumber.trim() || startOutboundCall.isLoading}
                        className="flex items-center gap-1.5 h-10 px-4 rounded-lg text-sm font-medium transition-colors border bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/50 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Icon
                          name="phone-outbound"
                          size={14}
                        />
                        {startOutboundCall.isLoading ? "Dialing..." : "Dial"}
                      </button>
                    </View>
                  </View>
                  {startOutboundCall.data && (
                    <View className="flex items-center gap-2 p-3 bg-green-100 dark:bg-green-900/30 rounded-lg text-green-700 dark:text-green-400">
                      <Icon
                        name="check-circle"
                        size={16}
                      />
                      <Text className="text-sm">Call initiated! Check your phone.</Text>
                    </View>
                  )}
                  {startOutboundCall.error && (
                    <View className="flex items-center gap-2 p-3 bg-red-100 dark:bg-red-900/30 rounded-lg text-red-700 dark:text-red-400">
                      <Icon
                        name="error"
                        size={16}
                      />
                      <Text className="text-sm">Failed to initiate call. Please try again.</Text>
                    </View>
                  )}
                </>
              )}
            </View>
          )}

          {/* Agent Monitoring */}
          <View className="flex-1 overflow-y-auto">
            <AgentMonitoring />
          </View>

          {/* Text Input (always visible) */}
          <View className="mt-auto p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
            <View className="relative flex w-full">
              <InputField
                value={inputMessage}
                onChange={setInputMessage}
                placeholder={testMode === "web" && isConnected ? "Type your message..." : "Start a web session to type messages..."}
                className="flex-1 pr-12 h-14 min-w-0"
                onKeyDown={onKeyDown}
                readOnly={testMode !== "web" || !isConnected}
              />
              <View className="absolute right-2 top-1/2 transform -translate-y-1/2">
                <button
                  onClick={testMode === "web" && isConnected ? onClickSendMessage : undefined}
                  type="button"
                  disabled={testMode !== "web" || !isConnected || !inputMessage.trim()}
                  className={clsx(
                    "flex items-center justify-center w-10 h-10 flex-shrink-0 rounded-lg transition-colors border",
                    testMode !== "web" || !isConnected
                      ? "bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700 cursor-not-allowed opacity-40"
                      : !inputMessage.trim()
                        ? "bg-blue-50 dark:bg-blue-950/40 text-blue-400 dark:text-blue-500 border-blue-200 dark:border-blue-800 cursor-not-allowed opacity-60"
                        : "bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/50",
                  )}
                >
                  <Icon
                    name="send"
                    size={16}
                  />
                </button>
              </View>
            </View>
          </View>
        </View>
      )}
    </View>
  );
};

type AgentDebuggerProps = {
  agentId: string;
  functionId: string;
  isOpen: boolean;
  onClick: () => void;
};

export const AgentDebugger = ({ agentId, functionId, isOpen, onClick }: AgentDebuggerProps) => {
  const [environmentId, setEnvironmentId] = useState("");
  const [config, setConfig] = useState<WebSocketClientConfig | undefined>();
  const agentEnvironment = useAppSelector((state) => (environmentId ? state.agentEnvironment.entities[environmentId] : null));

  const onChangeEnvironment = (id: string) => {
    setEnvironmentId(id);
  };

  // Ensure config is set only once or upon certain changes.
  // Passing a new config object on every render can cause issues with the audio worklet.
  useEffect(() => {
    setConfig({
      apiUrl: API_URL,
      agentId,
      environment: `${slugify(agentEnvironment?.name || "")}|${functionId}`,
      logLevel: "INFO",
      canary: true,
      origin: "debugger",
    });
  }, [agentEnvironment, functionId, agentId]);

  return config ? (
    <PrimVoicesProvider config={config}>
      <AgentDebuggerInternal
        functionId={functionId}
        environmentId={environmentId}
        isOpen={isOpen}
        onClick={onClick}
        onChangeEnvironment={onChangeEnvironment}
      />
    </PrimVoicesProvider>
  ) : null;
};
